import {
  Inject,
  Injectable,
  InjectionToken,
  ModuleWithProviders,
  NgModule,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import * as Rx from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  BackendDeletePayloadTypes,
  BackendFetchTypes,
  BackendPushPayloadTypes,
  BackendSetPayloadTypes,
  BackendUpdatePayloadTypes,
} from '../backend.model';
import { Collections, SearchCollections } from '../collections.model';
import {
  CollectionActions,
  DeleteRequestPayload,
  PushRequestPayload,
  SetRequestPayload,
  UpdateRequestPayload,
} from './actions';
import { ApiFetchResponse, ApiHttp, ApiPostResponse } from './api-http.service';
import { assertNever, isDefined } from './helpers';
import { DatastoreMissingModuleError } from './missing-module-error';
import { Ordering } from './query';
import { pluckItemFromRawStore } from './store.helpers';
import {
  Path,
  RawQuery,
  Reference,
  SearchQuery,
  StoreState,
} from './store.model';

export interface BackendSuccessResponse {
  readonly status: 'success';
}

export interface BackendErrorResponse {
  readonly status: 'error';
  readonly errorCode: ErrorCodeApi | 'UNKNOWN_ERROR';
  readonly requestId?: string;
}

export type BackendResponse = BackendSuccessResponse | BackendErrorResponse;

export interface Params {
  readonly [key: string]:
    | string
    | number
    | ReadonlyArray<number | undefined>
    | ReadonlyArray<string | undefined>
    | undefined;
}

export interface BackendFetchRequest {
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
}

type FetchRequestFactory<K extends keyof Collections> = ((
  authUid: string,
  ids: ReadonlyArray<string> | undefined,
  query: RawQuery<Collections[K]> | undefined,
  order: Ordering<K> | undefined,
) => BackendFetchRequest);

export interface BackendPushRequest<K extends keyof Collections> {
  readonly payload: BackendPushPayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  readonly asFormData?: boolean;
}

type PushRequestFactory<K extends keyof Collections> = ((
  authUid: string,
  entity: Collections[K],
  extra: { readonly [index: string]: string | number } | undefined,
) => BackendPushRequest<K>);

export interface BackendSetRequest<K extends keyof Collections> {
  readonly payload: BackendSetPayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  readonly asFormData?: boolean;
}

type SetRequestFactory<K extends keyof Collections> = ((
  authUid: string,
  entity: Collections[K],
) => BackendSetRequest<K>);

export interface BackendUpdateRequest<K extends keyof Collections> {
  readonly payload: BackendUpdatePayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  readonly asFormData?: boolean;
  readonly method: 'POST' | 'PUT';
}

type UpdateRequestFactory<K extends keyof Collections> = ((
  authUid: string,
  entity: Partial<Collections[K]>,
  originalEntity: Collections[K],
) => BackendUpdateRequest<K>);

export interface BackendDeleteRequest<K extends keyof Collections> {
  readonly payload: BackendDeletePayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  readonly asFormData?: boolean;
  readonly method: 'POST' | 'PUT' | 'DELETE';
}

type DeleteRequestFactory<K extends keyof Collections> = ((
  authUid: string,
  id: string | number,
) => BackendDeleteRequest<K>);

// We only want to sort on numeric or string fields, not undefined ones

export interface Backend<K extends keyof Collections> {
  readonly fetch?: FetchRequestFactory<K>;
  readonly push?: PushRequestFactory<K>;
  readonly set?: SetRequestFactory<K>;
  readonly update?: UpdateRequestFactory<K>;
  readonly remove?: DeleteRequestFactory<K>;
  readonly defaultOrder: Ordering<K>;
  readonly maxBatchSize?: number;
}

export const BACKEND_DEFAULT_BATCH_SIZE = 100;

export interface BackendSearch {
  readonly search: ((query: SearchQuery) => BackendFetchRequest);
}

export type BackendConfigs = { [K in keyof Collections]?: Backend<K> } &
  { [K in keyof SearchCollections]?: BackendSearch };

@Injectable()
export class StoreBackend {
  private backendConfigs: BackendConfigs = {};

  constructor(private store: Store<StoreState>, private apiHttp: ApiHttp) {}

  defaultOrder<K extends keyof Collections>(collection: K): Ordering<K> {
    const config = this.backendConfigs[collection] as Backend<K> | undefined;
    if (!config) {
      throw new DatastoreMissingModuleError(collection);
    }
    return config.defaultOrder;
  }

  batchSize<K extends keyof Collections>(ref: Reference<K>): number {
    const config = this.backendConfigs[ref.path.collection];
    return (config && config.maxBatchSize) || BACKEND_DEFAULT_BATCH_SIZE;
  }

  fetch<K extends keyof Collections>(
    ref: Reference<K>,
  ): Rx.Observable<ApiFetchResponse<K>> {
    const { path, query, order } = ref;
    const type = path.collection;

    return Rx.of(<Backend<K> | undefined>this.backendConfigs[type]).pipe(
      filter(isDefined),
      map(config => config.fetch),
      filter(isDefined),
      map(fetch => fetch(path.authUid, path.ids, query, order)),
      switchMap(request => this.apiHttp.fetch(request, query)),
    );
  }

  search<K extends keyof SearchCollections>(
    key: K,
    query: Rx.Observable<SearchQuery>,
  ): Rx.Observable<BackendFetchTypes[K]> {
    return query.pipe(
      map(q => (<BackendSearch>this.backendConfigs[key]).search(q)),
      switchMap(request => this.apiHttp.fetch(request)),
      map(
        response =>
          <BackendFetchTypes[K]>(response.status === 'success'
            ? response.result
            : []),
      ), // FIXME: Error handling?
    );
  }

  push<K extends keyof Collections>(
    ref: Reference<K>,
    object: Collections[K],
    extra?: { readonly [index: string]: string | number },
  ): Rx.Observable<BackendResponse> {
    const { path, query } = ref;
    const type = path.collection;

    return Rx.of(<Backend<K> | undefined>this.backendConfigs[type]).pipe(
      filter(isDefined),
      map(config => config.push),
      filter(isDefined),
      map(push => {
        const request = push(path.authUid, object, extra);
        return {
          request,
          payload: {
            type,
            ref,
            object,
            rawRequest: request.payload,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_PUSH`, payload };
        this.store.dispatch(action);
      }),
      switchMap(
        ({ request }) => this.apiHttp.post(request),
        ({ payload }, result) => ({ payload, result }),
      ),
      map(({ payload, result }) =>
        this.sendActions({ type: 'PUSH', payload }, path, query, result),
      ),
    );
  }

  set<K extends keyof Collections>(
    ref: Reference<K>,
    id: number | string,
    object: Collections[K],
  ): Rx.Observable<BackendResponse> {
    const { path, query } = ref;
    const type = path.collection;

    return Rx.of(<Backend<K> | undefined>this.backendConfigs[type]).pipe(
      filter(isDefined),
      map(config => config.set),
      filter(isDefined),
      withLatestFrom(this.store),
      map(([set, storeState]) => {
        const originalObject = pluckItemFromRawStore(storeState, path, id);
        if (originalObject === undefined) {
          throw new Error('Missing original object');
        }
        const request = set(path.authUid, object);
        return {
          request,
          payload: {
            type,
            ref,
            object,
            rawRequest: request.payload,
            originalObject,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_SET`, payload };
        this.store.dispatch(action);
      }),
      switchMap(
        ({ request, payload }) => this.apiHttp.post(request),
        ({ payload }, result) => ({ payload, result }),
      ),
      map(({ payload, result }) =>
        this.sendActions<K>({ type: 'SET', payload }, path, query, result),
      ),
    );
  }

  update<K extends keyof Collections>(
    ref: Reference<K>,
    id: number | string,
    object: Partial<Collections[K]>,
  ): Rx.Observable<BackendResponse> {
    const { path } = ref;
    const type = path.collection;

    return Rx.of(<Backend<K> | undefined>this.backendConfigs[type]).pipe(
      filter(isDefined),
      map(config => config.update),
      filter(isDefined),
      withLatestFrom(this.store),
      map(([update, storeState]) => {
        const originalObject = pluckItemFromRawStore(storeState, path, id);
        if (originalObject === undefined) {
          throw new Error('Missing original object');
        }
        const request = update(path.authUid, object, originalObject);
        return {
          request,
          payload: {
            type,
            ref,
            object,
            rawRequest: request.payload,
            originalObject,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_UPDATE`, payload };
        this.store.dispatch(action);
      }),
      switchMap(
        ({ request, payload }) => {
          switch (request.method) {
            case 'POST':
              return this.apiHttp.post(request);
            case 'PUT':
              return this.apiHttp.put(request);
            default:
              return assertNever(request.method);
          }
        },
        ({ payload }, result) => ({ payload, result }),
      ),
      map(({ payload, result }) =>
        this.sendActions<K>(
          { type: 'UPDATE', payload },
          payload.ref.path,
          payload.ref.query,
          result,
        ),
      ),
    );
  }

  delete<K extends keyof Collections>(
    ref: Reference<K>,
    id: number | string,
  ): Rx.Observable<BackendResponse> {
    const { path } = ref;
    const type = path.collection;

    return Rx.of(<Backend<K> | undefined>this.backendConfigs[type]).pipe(
      filter(isDefined),
      map(config => config.remove),
      filter(isDefined),
      withLatestFrom(this.store),
      map(([del, storeState]) => {
        const originalObject = pluckItemFromRawStore(storeState, path, id);
        if (originalObject === undefined) {
          throw new Error('Missing original object');
        }
        const request = del(path.authUid, id);
        return {
          request,
          payload: {
            type,
            ref,
            rawRequest: request.payload,
            originalObject,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_DELETE`, payload };
        this.store.dispatch(action);
      }),
      switchMap(
        ({ request, payload }) => {
          switch (request.method) {
            case 'POST':
              return this.apiHttp.post(request);
            case 'PUT':
              return this.apiHttp.put(request);
            case 'DELETE':
              return this.apiHttp.delete(request);
            default:
              return assertNever(request.method);
          }
        },
        ({ payload }, result) => ({ payload, result }),
      ),
      map(({ payload, result }) =>
        this.sendActions<K>(
          { type: 'DELETE', payload },
          payload.ref.path,
          payload.ref.query,
          result,
        ),
      ),
    );
  }

  private sendActions<K extends keyof Collections>(
    baseAction:
      | { readonly type: 'PUSH'; readonly payload: PushRequestPayload<K> }
      | { readonly type: 'SET'; readonly payload: SetRequestPayload<K> }
      | { readonly type: 'UPDATE'; readonly payload: UpdateRequestPayload<K> }
      | { readonly type: 'DELETE'; readonly payload: DeleteRequestPayload<K> },
    path: Path<K>,
    query: RawQuery<Collections[K]> | undefined,
    data: ApiPostResponse<K>,
  ): BackendResponse {
    switch (data.status) {
      case 'success': {
        const action: CollectionActions<K> =
          baseAction.type === 'PUSH'
            ? {
                type: 'API_PUSH_SUCCESS',
                payload: {
                  type: baseAction.payload.type,
                  ref: baseAction.payload.ref,
                  object: baseAction.payload.object,
                  rawRequest: baseAction.payload.rawRequest,
                  result: data.result,
                },
              }
            : baseAction.type === 'SET'
              ? {
                  type: 'API_SET_SUCCESS',
                  payload: {
                    type: baseAction.payload.type,
                    ref: baseAction.payload.ref,
                    object: baseAction.payload.object,
                    originalObject: baseAction.payload.originalObject,
                    rawRequest: baseAction.payload.rawRequest,
                    result: data.result,
                  },
                }
              : baseAction.type === 'UPDATE'
                ? {
                    type: 'API_UPDATE_SUCCESS',
                    payload: {
                      type: baseAction.payload.type,
                      ref: baseAction.payload.ref,
                      object: baseAction.payload.object,
                      originalObject: baseAction.payload.originalObject,
                      rawRequest: baseAction.payload.rawRequest,
                      result: data.result,
                    },
                  }
                : {
                    type: 'API_DELETE_SUCCESS',
                    payload: {
                      type: baseAction.payload.type,
                      ref: baseAction.payload.ref,
                      originalObject: baseAction.payload.originalObject,
                      rawRequest: baseAction.payload.rawRequest,
                      result: data.result,
                    },
                  };
        this.store.dispatch(action);
        return { status: 'success' };
      }
      default: {
        const action: CollectionActions<K> =
          baseAction.type === 'PUSH'
            ? {
                type: 'API_PUSH_ERROR',
                payload: baseAction.payload,
              }
            : baseAction.type === 'SET'
              ? {
                  type: 'API_SET_ERROR',
                  payload: baseAction.payload,
                }
              : baseAction.type === 'UPDATE'
                ? {
                    type: 'API_UPDATE_ERROR',
                    payload: baseAction.payload,
                  }
                : {
                    type: 'API_DELETE_ERROR',
                    payload: baseAction.payload,
                  };
        this.store.dispatch(action);
        return data;
      }
    }
  }

  addFeature<K extends keyof Collections>(
    collectionName: K,
    requestFactory: Backend<K>,
  ): void;

  addFeature<K extends keyof SearchCollections>(
    collectionName: K,
    requestFactory: BackendSearch,
  ): void;

  addFeature<K extends keyof (Collections & SearchCollections)>(
    collectionName: K,
    requestFactory: any,
  ): void {
    this.backendConfigs[collectionName] = requestFactory;
  }
}

export const BACKEND_COLLECTIONS = new InjectionToken<ReadonlyArray<string>>(
  'Backend Collections',
);

export const BACKEND_CONFIGS = new InjectionToken<SearchConfigsProvider>(
  'Backend Configs',
);

type BackendConfigFactory<K extends keyof Collections> = (() => Backend<K>);

type BackendSearchConfigFactory = (() => BackendSearch);

export type SearchConfigsProvider = ReadonlyArray<Backend<keyof Collections>> &
  ReadonlyArray<BackendSearch>;

@NgModule({})
export class BackendRootModule {}

@NgModule({})
export class BackendFeatureModule<
  K extends keyof (Collections & SearchCollections)
> {
  constructor(
    storeBackend: StoreBackend,
    @Inject(BACKEND_COLLECTIONS) backendCollections: ReadonlyArray<K>,
    @Inject(BACKEND_CONFIGS) backendConfigs: SearchConfigsProvider,
  ) {
    backendCollections.map((collectionName, index) => {
      storeBackend.addFeature(<any>collectionName, backendConfigs[index]); // FIXME: any type
    });
  }
}

@NgModule({})
export class BackendModule {
  static forRoot(): ModuleWithProviders {
    return {
      ngModule: BackendRootModule,
      providers: [StoreBackend],
    };
  }

  static forFeature<K extends keyof Collections>(
    collectionName: K,
    configFactory: BackendConfigFactory<K>,
  ): ModuleWithProviders;

  static forFeature<K extends keyof SearchCollections>(
    collectionName: K,
    configFactory: BackendSearchConfigFactory,
  ): ModuleWithProviders;

  static forFeature<K extends keyof (Collections & SearchCollections)>(
    collectionName: K,
    configFactory: any,
  ): ModuleWithProviders {
    return {
      ngModule: BackendFeatureModule,
      providers: [
        {
          provide: BACKEND_COLLECTIONS,
          multi: true,
          useValue: collectionName,
        },
        {
          provide: BACKEND_CONFIGS,
          multi: true,
          useFactory: configFactory,
        },
      ],
    };
  }
}
